import { ArrowBack } from '@mui/icons-material';
import {
  Alert,
  Box,
  Container,
  Grid,
  IconButton,
  Snackbar,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getSingleProduct } from '../../../api/products';
import Loading from '../../../components/Loading/loading';
import ProductPicture from '../../../components/product-details/product-picture/ProductPicture';
import ProductSpecs from '../../../components/product-details/product-specs/ProductSpecsSection';
import ViewProduct from '../../../components/product-details/view-product/ViewProduct';
import DashboardLayout from '../../../layout/dashboard/DashboardLayout';
import useAuthToken from '../../../hooks/getUserData';
import ProductComparison from '../../../components/product-details/product-comparison/ProductComparisonSection';
import ProductProvider from '../../../components/product-details/product-provider/ProductProviderSection';
import ProductReviews from '../../../components/product-details/product-reviews/ProductReviewSection';
import YoutubeVideos from '../../../components/product-details/product-youtube/YoutubeVideosSection';

const ProductDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const token = useAuthToken();
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleGoBack = () => {
    navigate('/products');
  };

  async function fetchProduct() {
    try {
      if (token) {
        const response = await getSingleProduct(id, token);
        if (!response.ok) {
          throw new Error('Failed to fetch product');
        }
        const data = await response.json();
        setProduct(data);
        setError(null);
      }
    } catch (error) {
      setError('An error occurred while fetching product');
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    fetchProduct();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardLayout>
      <Container maxWidth="xl">
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
          <IconButton size="large" onClick={handleGoBack}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h5">
            {!loading && product?.title?.charAt(0).toUpperCase() + product?.title?.slice(1)}
          </Typography>
        </Box>
        {loading ? (
          <Loading />
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : (
          <>
            <Snackbar
              anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              open={snackbarOpen}
              autoHideDuration={2000}
              onClose={() => setSnackbarOpen(false)}>
              <Alert severity="success" sx={{ width: '100%' }}>
                {snackbarMessage}
              </Alert>
            </Snackbar>
            {product && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <Grid item sx={{ mb: 2 }}>
                    <ProductPicture
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                  <Grid item sx={{ mb: 2 }}>
                    <ProductSpecs
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                  <Grid item sx={{ mb: 2 }}>
                    <ProductComparison
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                  <Grid item>
                    <YoutubeVideos
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12} md={5}>
                  <Grid item>
                    <ViewProduct
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                  <Grid item sx={{ mb: 2 }}>
                    <ProductProvider
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                  <Grid item>
                    <ProductReviews
                      product={product}
                      fetchProduct={fetchProduct}
                      setMessage={setSnackbarMessage}
                      setSnackbarOpen={setSnackbarOpen}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </Container>
    </DashboardLayout>
  );
};

export default ProductDetails;
